import { Button } from '../../../../components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import Input from '../../../../components/Input';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { User } from '../types/Organization';
import Select from '@/components/Select';
import { FiEye, FiEyeOff } from 'react-icons/fi';

import AvatarEditorModal from '../../candidates/components/AvatarEditorModal';
import useFetchIam from '../../api/useFetchIam';
import useUpdateOrganizationStaff from '../api/useUpdateOrganizationStaff';
import useUpdateOrganizationStaffResetPassword from '../api/useUpdateOrganizationStaffResetPassword';
import useUpdateOrganizationStaffAttachAvatar from '../api/useUpdateOrganizationStaffAttachAvatar';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

const USER_TYPE_OPTIONS = [
  {
    value: 'admin',
    label: '管理者ユーザー',
    comment: 'ユーザーの招待/削除など、全てのアクセスが可能です。',
  },
  {
    value: 'normal',
    label: '通常ユーザー',
    comment:
      'ユーザーの招待/削除、データのエクスポート、APIキーの出力ができません。キャリアアドバイザーのメンバーに最適です。',
  },
];

interface StaffFormProps {
  currentUser?: User;
  updateInfo?: (name: string, email: string, is_admin: boolean, avatar?: File | null) => void;
  updatePassword?: (password: string, password_confirmation: string) => void;
  resetPasswordFields?: boolean;
  onClose?: () => void;
}

export default function StaffForm({
  currentUser,
  updateInfo,
  updatePassword,
  resetPasswordFields,
  onClose,
}: StaffFormProps) {
  const [staff, setStaff] = useState<User>(
    currentUser || {
      id: 0,
      name: '',
      email: '',
      is_admin: true,
      status: 'active',
      avatar_url: '',
      is_support: false,
      order_number: null,
    }
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [tempAvatarSrc, setTempAvatarSrc] = useState<string>('');
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const { data: iam } = useFetchIam();
  const { mutateAsync: updateInfoAPI } = useUpdateOrganizationStaff();
  const { mutateAsync: updatePasswordAPI } = useUpdateOrganizationStaffResetPassword();
  const { mutateAsync: updateAvatarAPI } = useUpdateOrganizationStaffAttachAvatar();

  // 管理者でないユーザーが自分の情報を表示する場合、IAMから取得した情報を使用
  useEffect(() => {
    if (!currentUser && iam?.current_user) {
      setStaff({
        id: iam.current_user.id,
        name: iam.current_user.name,
        email: iam.current_user.email || '',
        is_admin: iam.current_user.is_admin,
        status: 'active',
        avatar_url: iam.current_user.avatar_url || '',
        is_support: iam.current_user.is_support,
        order_number: iam.current_user.order_number,
      });
    }
  }, [iam?.current_user, currentUser]);

  const currentAvatarUrl =
    avatarPreview || currentUser?.avatar_url || iam?.current_user?.avatar_url || '';

  // バリデーション関数
  const validateProfileForm = () => {
    const newErrors: Pick<typeof errors, 'name' | 'email'> = {};

    // 名前のバリデーション
    if (!staff.name.trim()) {
      newErrors.name = '氏名を入力してください';
    } else if (staff.name.trim().length < 2) {
      newErrors.name = '氏名は2文字以上で入力してください';
    } else if (staff.name.trim().length > 15) {
      newErrors.name = '氏名は15文字以下で入力してください';
    }

    // メールアドレスのバリデーション
    if (!staff.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(staff.email.trim())) {
        newErrors.email = '正しいメールアドレスの形式で入力してください';
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors: Pick<typeof errors, 'password' | 'confirmPassword'> = {};

    // パスワードのバリデーション
    if (!newPassword) {
      newErrors.password = 'パスワードを入力してください';
    } else if (newPassword.length < 8) {
      newErrors.password = 'パスワードは8文字以上で入力してください';
    }

    // 確認用パスワードのバリデーション
    if (!confirmPassword) {
      newErrors.confirmPassword = '確認用パスワードを入力してください';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'パスワードが一致していません';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStaff({ ...staff, name: e.target.value });
    // エラーをクリア
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStaff({ ...staff, email: e.target.value });
    // エラーをクリア
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    // エラーをクリア
    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    // エラーをクリア
    if (errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setTempAvatarSrc(event.target.result as string);
          setShowAvatarModal(true);
        }
      };
      reader.readAsDataURL(file);
    }
    // ファイル選択をリセット
    e.target.value = '';
  };

  const handleSaveAvatar = (croppedImage: string) => {
    // Base64からFileオブジェクトを作成
    const base64Data = croppedImage.split(',')[1];
    const binaryData = atob(base64Data);
    const array = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      array[i] = binaryData.charCodeAt(i);
    }
    const blob = new Blob([array], { type: 'image/jpeg' });
    const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });

    setSelectedAvatar(file);
    setAvatarPreview(croppedImage);
    setShowAvatarModal(false);
  };

  const handleCancelAvatar = () => {
    setShowAvatarModal(false);
    setTempAvatarSrc('');
  };

  const handleSave = async () => {
    // バリデーションチェック
    if (!validateProfileForm()) {
      return;
    }

    // 管理者でないユーザーが自分の情報を編集している場合は、内部でAPI実行
    if (!iam?.current_user?.is_admin && iam?.current_user?.id === staff.id) {
      try {
        if (selectedAvatar) {
          await updateAvatarAPI({ id: staff.id, avatar: selectedAvatar });
        }
        await updateInfoAPI({
          id: staff.id,
          name: staff.name.trim(),
          email: staff.email.trim(),
          is_admin: staff.is_admin,
        });
        showSuccessToast('登録情報を保存しました。');
      } catch (error) {
        showErrorToast('登録情報を保存できませんでした。');
      }
    } else {
      // 管理者の場合は従来通りpropsの関数を使用
      updateInfo?.(staff.name.trim(), staff.email.trim(), staff.is_admin, selectedAvatar);
    }
  };

  useEffect(() => {
    setNewPassword('');
    setConfirmPassword('');
    // パスワード関連のエラーもクリア
    setErrors((prev) => ({ ...prev, password: undefined, confirmPassword: undefined }));
  }, [resetPasswordFields]);

  const handleUpdatePassword = async () => {
    // バリデーションチェック
    if (!validatePasswordForm()) {
      return;
    }

    // 管理者でないユーザーが自分の情報を編集している場合は、内部でAPI実行
    if (!iam?.current_user?.is_admin && iam?.current_user?.id === staff.id) {
      try {
        await updatePasswordAPI({
          id: staff.id,
          password: newPassword,
          password_confirmation: confirmPassword,
        });
        showSuccessToast('パスワードを変更しました。');
        setNewPassword('');
        setConfirmPassword('');
      } catch (error) {
        showErrorToast('パスワードを変更できませんでした。');
      }
    } else {
      // 管理者の場合は従来通りpropsの関数を使用
      updatePassword?.(newPassword, confirmPassword);
    }
  };

  const isEditable = currentUser?.is_admin || currentUser?.id === staff.id;

  return (
    <>
      {/* メンバー編集の時のみ表示 */}
      {iam?.current_user?.id !== staff.id && (
        <button
          type="button"
          className="mt-5 mb-8 flex items-center pl-6 text-sm font-bold text-primary hover:text-primary_darken"
          onClick={onClose}
        >
          <MaterialIcon name="arrow_back_ios" className="mr-1 text-base" />
          メンバー管理に戻る
        </button>
      )}
      {/* プロフィール情報 */}
      <div className="rounded-lg bg-white px-12 py-11">
        <div className="flex gap-x-8">
          <div className="w-[140px]">
            <div className="mb-6 flex flex-col items-center">
              <div className="size-[122px] overflow-hidden rounded-full bg-[#EBF1FF]">
                <img src={currentAvatarUrl} alt="アイコン" className="size-full object-cover" />
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
              <button
                type="button"
                className="mt-4 flex items-center text-sm font-bold text-primary hover:text-[#005effb5]"
                onClick={() => fileInputRef.current?.click()}
              >
                アイコンを変更する
                <MaterialIcon name="arrow_forward_ios" className="pl-1 text-[10px] text-primary" />
              </button>
            </div>
          </div>
          <div className="flex-1">
            <div className="mb-6">
              <label htmlFor="name" className="block text-body-sm font-medium">
                氏名
              </label>
              <Input
                id="name"
                type="text"
                value={staff.name}
                onChange={handleNameChange}
                placeholder="氏名"
                required
                className={`mt-2 w-full max-w-[400px] ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name ? (
                <p className="mt-1 text-xs text-red-600">{errors.name}</p>
              ) : (
                <p className="mt-1 text-xs text-sub">2文字以上で入力してください</p>
              )}
            </div>

            <div className="mb-6">
              <label htmlFor="email" className="block text-body-sm font-medium">
                メールアドレス
              </label>
              <Input
                id="email"
                type="email"
                value={staff.email}
                onChange={handleEmailChange}
                placeholder=""
                required
                className={`mt-2 w-full max-w-[400px] ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            <fieldset disabled={!isEditable}>
              <div>
                <label htmlFor="userType" className="block text-body-sm font-medium">
                  アクセス権限
                </label>
                <div className="mt-1 w-[236px]">
                  <Select
                    variant="compact"
                    value={staff.is_admin ? 'admin' : 'normal'}
                    onValueChange={(value) => setStaff({ ...staff, is_admin: value === 'admin' })}
                    items={USER_TYPE_OPTIONS}
                  />
                </div>
              </div>
            </fieldset>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            color="primary"
            size="md"
            disabled={staff.name.length < 2 || !staff.email}
            onClick={handleSave}
          >
            保存する
          </Button>
        </div>
      </div>

      {/* パスワード変更 */}
      <div className="mt-8 flex gap-8 rounded-lg bg-white px-12 py-11">
        <h2 className="mb-6 w-[140px] text-base font-bold">パスワードの変更</h2>
        <div className="flex-1">
          <div className="space-y-8">
            <div>
              <label htmlFor="newPassword" className="block text-body-sm font-medium">
                新規パスワード
              </label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  placeholder="新規パスワード"
                  required
                  className={`mt-2 w-full max-w-[400px] ${errors.password ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  className="absolute bottom-[7px] left-[365px] text-2xl text-gray-400"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                >
                  {showPassword ? <FiEye /> : <FiEyeOff />}
                </button>
              </div>
              {errors.password ? (
                <p className="mt-1 text-xs text-red-600">{errors.password}</p>
              ) : (
                <p className="mt-1 text-xs text-sub">8文字以上で入力してください。</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-body-sm font-medium">
                新規パスワード（確認用）
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="新規パスワード（確認用）"
                  required
                  className={`mt-2 w-full max-w-[400px] ${errors.confirmPassword ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  className="absolute bottom-[7px] left-[365px] text-2xl text-gray-400"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
          <div className="mt-8 flex justify-end">
            <Button
              color="primary"
              size="md"
              disabled={!newPassword || !confirmPassword}
              onClick={handleUpdatePassword}
            >
              保存する
            </Button>
          </div>
        </div>
      </div>

      {/* Avatar Editor Modal */}
      {showAvatarModal && (
        <AvatarEditorModal
          src={tempAvatarSrc}
          onClose={handleCancelAvatar}
          onSave={handleSaveAvatar}
        />
      )}
    </>
  );
}
